/**
 * Transport for the conversation backend.
 *
 * Streams tokens as they arrive rather than buffering the whole reply. The
 * reference implementation consumed the entire stream internally and then faked
 * a typewriter afterwards; surfacing real tokens cuts perceived latency, which
 * is the most visible quality signal in a face-to-face conversation.
 *
 * Errors are thrown, not swallowed. The caller decides how to show them.
 */
import { CONFIG } from '../config.js';
import { SseParser } from './sse.js';
import type { ChatEvent, ChatReply, ChatRequest } from './types.js';

export interface StreamHandlers {
  /** Called for each token as it arrives. */
  onToken?(chunk: string, accumulated: string): void;
}

export class BackendGateway {
  /**
   * Conversation memory is server-side and keyed on this. A reload starts a
   * fresh conversation, which is the honest behaviour — the client keeps no
   * history the server could be resynchronised against.
   */
  readonly sessionId: string = crypto.randomUUID();

  constructor(private readonly apiBaseUrl: string) {}

  async send(message: string, handlers: StreamHandlers = {}): Promise<ChatReply> {
    const url = `${this.apiBaseUrl}/chat`;
    const body: ChatRequest = { message, sessionId: this.sessionId };

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), CONFIG.chat.requestTimeoutMs);

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (error) {
      clearTimeout(timeout);
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error(`The guide took too long to answer.`);
      }
      throw new Error(`Could not reach the guide. ${describe(error)}`);
    }

    try {
      if (!response.ok) {
        const detail = await safeText(response);
        throw new Error(`The guide returned ${response.status}. ${detail}`.trim());
      }
      if (!response.body) {
        throw new Error('The guide returned an empty response.');
      }
      return await this.consume(response.body, handlers);
    } finally {
      clearTimeout(timeout);
    }
  }

  private async consume(
    body: ReadableStream<Uint8Array>,
    handlers: StreamHandlers,
  ): Promise<ChatReply> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseParser();
    let accumulated = '';
    let settled: ChatReply | undefined;

    const handle = (raw: string): boolean => {
      const event = parseEvent(raw);
      if (!event) return false; // malformed frame — skip rather than abort
      if (event.type === 'token') {
        accumulated += event.content;
        handlers.onToken?.(event.content, accumulated);
        return false;
      }
      if (event.type === 'error') {
        throw new Error(event.message || 'The guide reported an error.');
      }
      settled = {
        // `done.message` is canonical when present: it lets the server correct
        // the accumulated text (trimming, punctuation) in one place.
        message: clampLength(event.message ?? accumulated),
        followUps: (event.followUps ?? []).slice(0, CONFIG.chat.maxFollowUps),
        citations: event.citations ?? [],
        animationHint: event.animationHint ?? 'speaking',
        ...(event.animationClip ? { animationClip: event.animationClip } : {}),
      };
      return true;
    };

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (value) {
          for (const raw of parser.push(decoder.decode(value, { stream: true }))) {
            if (handle(raw.data)) return settled!;
          }
        }
        if (done) break;
      }
      for (const raw of parser.flush()) {
        if (handle(raw.data)) return settled!;
      }
    } finally {
      reader.releaseLock();
    }

    // Stream ended without a `done`. Tokens still arrived, so show them rather
    // than throwing away a perfectly good answer over a missing terminator.
    if (accumulated.length > 0) {
      return {
        message: clampLength(accumulated),
        followUps: [],
        citations: [],
        animationHint: 'speaking',
      };
    }
    throw new Error('The guide sent nothing back.');
  }
}

function parseEvent(raw: string): ChatEvent | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const type = (parsed as { type?: unknown }).type;
    if (type !== 'token' && type !== 'done' && type !== 'error') return null;
    return parsed as ChatEvent;
  } catch {
    return null;
  }
}

/**
 * A fixed-size world-space bubble has nowhere to overflow, so an unbounded
 * reply would simply be cut off mid-sentence. Trim at a word boundary.
 */
function clampLength(text: string): string {
  const limit = CONFIG.chat.maxReplyChars;
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

async function safeText(response: Response): Promise<string> {
  try {
    return (await response.text()).slice(0, 200);
  } catch {
    return '';
  }
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
